import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Modal.module.css';

interface ModalProps {
  /** Names the dialog for a screen reader — it has no heading of its own to borrow. */
  label: string;
  onClose: () => void;
  /** How wide the surface may grow. `md` suits a form of short fields. */
  width?: 'md' | 'lg';
  /**
   * Whether a click on the backdrop closes it. The caller says so, because only the
   * caller knows whether anything would be lost: true while the form is untouched,
   * false once something has been typed.
   */
  dismissible?: boolean;
  children: ReactNode;
}

/**
 * A modal dialog, built on the native `<dialog>`.
 *
 * Native rather than a div with a high `z-index`, because `showModal()` is what puts
 * the element in the browser's **top layer** — and the top layer is where the focus
 * trap, the inert background, the Escape handling and the `::backdrop` come from. All
 * four are things a hand-rolled overlay has to re-earn, and the focus trap in
 * particular is the one everybody gets wrong.
 *
 * **Open state lives with the caller, not here.** This component is mounted when it
 * should be open and unmounted when it should not, so there is no `isOpen` prop and
 * nothing to keep in step. On both screens that state is a value in the URL.
 *
 * **A click on the backdrop closes it only when `dismissible`.** That click is the
 * usual way to dismiss a dialog, but everything this wraps is a form, and a stray
 * click beside it should not throw away what somebody has typed. So the caller turns
 * it off once the form holds a change; Escape and Cancel stay, as deliberate ways out.
 */
export const Modal = ({ label, onClose, width = 'md', dismissible = false, children }: ModalProps) => {
  const ref = useRef<HTMLDialogElement>(null);

  /**
   * Whether the press began on the backdrop. A click is dispatched to the element
   * that holds both the press and the release, so selecting text in a field and
   * letting go outside the card is a "click" on the dialog — and must not close it.
   */
  const pressedOutside = useRef(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;

    // Not the `open` attribute, which shows the dialog inline and in normal flow
    // with none of the modal behaviour.
    if (!dialog.open) dialog.showModal();

    // Closed on the way out so the browser gives focus back and stops treating the
    // rest of the page as inert. React removing the node is not enough on its own.
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={cx(styles.dialog, styles[width])}
      aria-label={label}
      onCancel={(event) => {
        // Escape. The browser would close the ELEMENT while the caller — whose URL
        // decides whether this is mounted at all — went on thinking it was open, and
        // the next render would put a closed dialog back on screen. So the default is
        // prevented and the close is routed through the caller instead.
        event.preventDefault();
        onClose();
      }}
      // The backdrop is not an element of its own: a click on it lands on the dialog
      // itself, while a click on the card lands on something inside it.
      onMouseDown={(event) => {
        pressedOutside.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (dismissible && pressedOutside.current && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.surface}>{children}</div>
    </dialog>
  );
};
